import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ApiError,
  createSchedule,
  getSchedule,
  listProducts,
  updateSchedule,
  type CreateScheduleDto,
  type UpdateScheduleDto,
} from '../lib/api-client';
import { schedulableProducts } from '../lib/schedulable-products';

/**
 * 班次新增 / 編輯（AC-A2-1 / AC-A2-3）。
 *
 * - 新增 `/schedules/new`：product / 出發 datetime / 結束 datetime / 容量。
 * - 編輯 `/schedules/:id`：僅允許改 capacity + weather_cancelled
 *   （AC-A2-1：建後不允改 product / 出發時間）。
 * - datetime 使用 <input type="datetime-local">（本地時區），送 BE 前 toISOString()。
 * - 容量 1..500（FE 限制；BE 僅檢 ≥1）。
 */
export function ScheduleEditPage(): JSX.Element {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = typeof params.id === 'string' && params.id.length > 0;
  const scheduleId = params.id ?? '';

  const products = useQuery({
    queryKey: ['products.list'],
    queryFn: () => listProducts({ limit: 200 }),
    enabled: !isEdit,
  });

  const existing = useQuery({
    queryKey: ['schedules.get', scheduleId],
    queryFn: () => getSchedule(scheduleId),
    enabled: isEdit,
  });

  // ---- form state ----
  const [productId, setProductId] = useState('');
  const [departureLocal, setDepartureLocal] = useState(''); // YYYY-MM-DDTHH:mm
  const [departureEndLocal, setDepartureEndLocal] = useState('');
  const [capacity, setCapacity] = useState<number>(50);
  const [weatherCancelled, setWeatherCancelled] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 編輯模式 載入既有值
  useEffect(() => {
    if (isEdit && existing.data) {
      const s = existing.data;
      setProductId(s.product_id);
      setDepartureLocal(toLocalDatetimeInput(s.departure_at));
      setDepartureEndLocal(toLocalDatetimeInput(s.departure_end_at));
      setCapacity(s.capacity);
      setWeatherCancelled(s.weather_cancelled);
      setCancellationReason(s.cancellation_reason ?? '');
    }
  }, [isEdit, existing.data]);

  const createMut = useMutation({
    mutationFn: (dto: CreateScheduleDto) => createSchedule(dto),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ['schedules.list'] });
      navigate(`/schedules?product_id=${encodeURIComponent(s.product_id)}`);
    },
  });

  const updateMut = useMutation({
    mutationFn: (dto: UpdateScheduleDto) => updateSchedule(scheduleId, dto),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ['schedules.list'] });
      void qc.invalidateQueries({ queryKey: ['schedules.get', scheduleId] });
      navigate(`/schedules?product_id=${encodeURIComponent(s.product_id)}`);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setSubmitError(null);

    if (!isEdit) {
      if (productId === '') {
        setSubmitError('請選擇商品');
        return;
      }
      if (departureLocal === '' || departureEndLocal === '') {
        setSubmitError('請填寫出發與結束時間');
        return;
      }
      const start = new Date(departureLocal);
      const end = new Date(departureEndLocal);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        setSubmitError('時間格式不正確');
        return;
      }
      if (end.getTime() <= start.getTime()) {
        setSubmitError('結束時間必須晚於出發時間');
        return;
      }
      if (!Number.isInteger(capacity) || capacity < 1 || capacity > 500) {
        setSubmitError('容量需為 1 至 500 之整數');
        return;
      }
      const dto: CreateScheduleDto = {
        product_id: productId,
        departure_at: start.toISOString(),
        departure_end_at: end.toISOString(),
        capacity,
      };
      createMut.mutate(dto);
      return;
    }

    // ---- edit 模式 ----
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 500) {
      setSubmitError('容量需為 1 至 500 之整數');
      return;
    }
    const dto: UpdateScheduleDto = {
      capacity,
      weather_cancelled: weatherCancelled,
    };
    if (weatherCancelled && cancellationReason.trim() !== '') {
      dto.cancellation_reason = cancellationReason.trim();
    }
    updateMut.mutate(dto);
  };

  const pending = createMut.isPending || updateMut.isPending;
  const mutationError = createMut.error ?? updateMut.error;

  const headerTitle = isEdit ? '編輯班次（AC-A2-1）' : '新增班次（AC-A2-1）';

  const productOptions = useMemo(() => products.data?.items ?? [], [products.data]);

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">{headerTitle}</h1>
        <div className="page-head__actions">
          <button onClick={() => navigate(-1)}>返回</button>
        </div>
      </div>

      {isEdit && existing.isLoading && <div>載入中…</div>}
      {isEdit && existing.error && (
        <div className="error-banner">{(existing.error as Error).message}</div>
      )}

      {(!isEdit || existing.data) && (
        <form onSubmit={onSubmit} style={{ maxWidth: 560, marginTop: 12 }}>
          <Field label="商品">
            {isEdit ? (
              <input
                value={productId}
                readOnly
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
              />
            ) : (
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                disabled={products.isLoading}
                style={{ width: '100%' }}
              >
                <option value="">— 請選擇 —</option>
                {schedulableProducts(productOptions).map((p) => (
                  <option key={p.id} value={p.id}>
                    [{p.sku}] {p.name_i18n['zh-TW'] ?? p.name_i18n['en'] ?? '(未命名)'}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="出發時間">
            <input
              type="datetime-local"
              value={departureLocal}
              onChange={(e) => setDepartureLocal(e.target.value)}
              readOnly={isEdit}
              required
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="結束時間">
            <input
              type="datetime-local"
              value={departureEndLocal}
              onChange={(e) => setDepartureEndLocal(e.target.value)}
              readOnly={isEdit}
              required
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="容量（1..500）">
            <input
              type="number"
              value={capacity}
              min={1}
              max={500}
              onChange={(e) => setCapacity(Number.parseInt(e.target.value, 10) || 0)}
              required
              style={{ width: '100%' }}
            />
          </Field>

          {isEdit && (
            <>
              <Field label="天候停航（AC-A2-3）">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={weatherCancelled}
                    onChange={(e) => setWeatherCancelled(e.target.checked)}
                  />
                  <span style={{ fontSize: 13 }}>標記此班次為停航</span>
                </label>
              </Field>
              {weatherCancelled && (
                <Field label="停航原因（選填，最多 200 字）">
                  <input
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    maxLength={200}
                    style={{ width: '100%' }}
                    placeholder="例：颱風海上警報"
                  />
                </Field>
              )}
            </>
          )}

          {submitError && <div className="error-banner">{submitError}</div>}
          {mutationError && (
            <div className="error-banner">
              {mutationError instanceof ApiError
                ? mutationError.message
                : (mutationError as Error).message}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="submit" className="primary" disabled={pending}>
              {pending ? '送出中…' : isEdit ? '儲存' : '建立'}
            </button>
            <button type="button" onClick={() => navigate(-1)} disabled={pending}>
              取消
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * ISO 字串 → datetime-local input 顯示值（本地時區，無秒、無時區後綴）。
 */
function toLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
